import * as React from 'react';
import { useEffectOnce } from 'react-use';
import { useSession } from '@ghq-abi/auth-client-lib/client';

import type { UserCustomData } from '~/pages/api/auth/[...nextauth]';

type AuthContextData = {
  proxyIn: (globalId: string) => Promise<void>;
  proxyOut: () => Promise<void>;
  isProxied: boolean;
};

type AuthProviderProps = {
  children: React.ReactNode;
};

const AuthContext = React.createContext({} as AuthContextData);

let authChannel: BroadcastChannel;

export function AuthProvider({ children }: AuthProviderProps) {
  const {
    proxyIn: libProxyIn,
    proxyOut: libProxyOut,
    data: session,
  } = useSession<UserCustomData>();

  const isProxied = !!session?.user?.proxiedAs;
  useEffectOnce(() => {
    function handleIncomingMessage(e: MessageEvent) {
      switch (e.data) {
        case 'changedProxy':
          window.location.reload();
          break;

        default:
          break;
      }
    }

    authChannel = new BroadcastChannel('@ghq/auth');
    authChannel.addEventListener('message', handleIncomingMessage);

    return () => {
      authChannel.removeEventListener('message', handleIncomingMessage);
    };
  });

  const proxyIn = React.useCallback(
    async (globalId: string) => {
      await libProxyIn(globalId);
      authChannel?.postMessage('changedProxy');
    },
    [libProxyIn]
  );

  const proxyOut = React.useCallback(async () => {
    await libProxyOut();
    authChannel?.postMessage('changedProxy');
  }, [libProxyOut]);

  const providerValue = React.useMemo(
    () => ({ proxyIn, proxyOut, isProxied }),
    [proxyIn, proxyOut, isProxied]
  );

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
